import { useEffect, useState } from "react";
import { Camera, Download, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { fetchJobPhotos, downloadJobPhotoBundle, rotateJobPhoto } from "../lib/api";
import { tenantLocale, tenantTz } from "../lib/company";

// `rotated_at` busts the browser cache — the URL is otherwise identical for
// the whole session, so a rotated photo would keep showing the old way round.
const photoSrc = (p) => {
  const t = localStorage.getItem("rescue_token");
  const v = p.rotated_at ? `&v=${encodeURIComponent(p.rotated_at)}` : "";
  return `${process.env.REACT_APP_BACKEND_URL}${p.url}${t ? `?t=${t}` : "?"}${v}`;
};

const sourceLabel = (p) => (p.source === "photoshop" ? "Yard (PhotoShop)" : p.uploader_role === "driver" ? "Driver" : "Office");

// Impound photo gallery — driver tow photos + PhotoShop yard captures.
export default function ImpoundPhotos({ job }) {
  const [photos, setPhotos] = useState(job?.photos || []);
  const [zoomed, setZoomed] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [rotating, setRotating] = useState(null);

  // Straighten a sideways photo without opening it. Rotates the STORED
  // pixels, so the insurer PDF and email attachments pick up the corrected
  // version too — the in-app camera writes no EXIF for anything downstream
  // to honour, so display-only rotation would fix nothing that leaves here.
  const rotate = async (p) => {
    setRotating(p.id);
    try {
      const r = await rotateJobPhoto(job.id, p.id, 1);
      setPhotos((cur) => cur.map((x) => (x.id === p.id ? { ...x, rotated_at: r.rotated_at } : x)));
      setZoomed((z) => (z && z.id === p.id ? { ...z, rotated_at: r.rotated_at } : z));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rotate that photo");
    } finally {
      setRotating(null);
    }
  };

  useEffect(() => {
    if (!job?.id) return;
    setPhotos(job.photos || []);
    fetchJobPhotos(job.id).then(setPhotos).catch(() => {
      // Keep whatever came with the job rather than blanking the gallery —
      // but say so, instead of looking like the vehicle has no photos.
      toast.error("Could not refresh the photo list");
    });
  }, [job?.id, job?.photos]);

  async function download() {
    setDownloading(true);
    try {
      const { blob, filename } = await downloadJobPhotoBundle(job.id);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Couldn't download photos");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div id={`impound-photos-${job?.id}`} className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50/50 dark:bg-slate-800/30" data-testid="impound-detail-photos">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          <Camera className="h-3 w-3" /> Photos ({photos.length})
        </div>
        {photos.length > 0 && (
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={download} disabled={downloading} data-testid="impound-photos-download-btn">
            {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Download
          </Button>
        )}
      </div>
      {photos.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-1" data-testid="impound-photos-empty">
          No photos yet. Driver tow photos and PhotoShop yard photos for this rego appear here.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid="impound-photos-grid">
          {photos.map((p) => (
            <div key={p.id} className="relative">
              <button
                type="button"
                onClick={() => setZoomed(p)}
                className="relative aspect-square w-full overflow-hidden rounded border border-slate-200 dark:border-slate-700 hover:border-blue-400 transition-colors"
                data-testid={`impound-photo-${p.id}`}
              >
                <img src={photoSrc(p)} alt="" className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[9px] px-1 py-0.5 truncate text-left">
                  {sourceLabel(p)}
                </span>
              </button>
              {/* Sits outside the tile button — nesting a button inside a
                  button is invalid and the inner one never fires. */}
              <button
                type="button"
                onClick={() => rotate(p)}
                disabled={rotating === p.id}
                title="Rotate a quarter turn clockwise"
                aria-label="Rotate photo"
                className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center shadow disabled:opacity-50"
                data-testid={`impound-photo-rotate-${p.id}`}
              >
                {rotating === p.id
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <RotateCw className="h-3.5 w-3.5" />}
              </button>
            </div>
          ))}
        </div>
      )}
      {zoomed && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center p-4" onClick={() => setZoomed(null)} data-testid="impound-photo-zoom">
          <img src={photoSrc(zoomed)} alt="" className="max-w-full max-h-[85vh] object-contain" />
          <div className="text-white/80 text-xs mt-2">
            {sourceLabel(zoomed)}
            {zoomed.taken_at && ` · ${new Date(zoomed.taken_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}`}
          </div>
        </div>
      )}
    </div>
  );
}
